import React from "react";
import { graphql, PageProps } from "gatsby";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import HeadingHighlight from "@/components/HeadingHighlight";
import ButtonCTA from "@/components/ButtonCTA";
import Breadcrumb from "@/components/Breadcrumb";
import { CRJMC_PRICE } from "@/constants";
import {
  Shield,
  Clock,
  FileCheck,
  AlertCircle,
  CheckCircle,
  HelpCircle,
} from "lucide-react";

interface RealEstateAgentsData {
  site: {
    siteMetadata: {
      siteUrl: string;
    };
  };
}

export const query = graphql`
  query RealEstateAgentsPage {
    site {
      siteMetadata {
        siteUrl
      }
    }
  }
`;

const RealEstateAgentsPage: React.FC<PageProps<RealEstateAgentsData>> = ({
  data,
}) => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main>
        {/* Hero Section */}
        <section
          className="relative py-10 px-4 bg-gradient-to-br from-primary to-primary-dark text-white overflow-hidden"
          aria-labelledby="hero-heading"
        >
          <div className="absolute inset-0 z-0 bg-black/40">
            <picture>
              <source
                srcSet="/images/real-estate-agent/cleared-background-check-real-estate-agent.avif"
                type="image/avif"
              />
              <source
                srcSet="/images/real-estate-agent/cleared-background-check-real-estate-agent.webp"
                type="image/webp"
              />
              <source
                srcSet="/images/real-estate-agent/cleared-background-check-real-estate-agent.jpg"
                type="image/jpeg"
              />
              <img
                src="/images/real-estate-agent/cleared-background-check-real-estate-agent.jpg"
                alt="Real estate agent with cleared background check"
                className="w-full h-full object-cover opacity-30"
                width="1920"
                height="1080"
              />
            </picture>
          </div>
          <div className="container mx-auto max-w-6xl relative z-10">
            <Breadcrumb
              items={[
                { label: "Ontario", href: "/ontario" },
                { label: "Real Estate Agents" },
              ]}
              variant="dark"
              textColor="text-gray-400"
              linkColor="hover:text-emerald-400"
            />
            <div className="text-center mt-10">
              <HeadingHighlight
                as="h1"
                size="4xl"
                className="mb-6 drop-shadow-sm text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight"
                id="hero-heading"
              >
                CRJMC Ontario Background Checks
                <br className="hidden sm:block" />
                &nbsp;for Real Estate Agent License
              </HeadingHighlight>
              <p className="text-base sm:text-lg md:text-xl mb-8 max-w-2xl mx-auto drop-shadow-sm">
                Apply for your CRJMC check online today. Fast, reliable criminal
                record checks for your RECO registration and license
                requirements.
              </p>
              <div className="inline-block">
                <ButtonCTA />
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 py-12">
          {/* Introduction */}
          <div className="mb-12">
            <p className="text-lg text-gray-700 mb-6">
              In Ontario, obtaining a Criminal Record and Judicial Matters Check
              (CRJMC) is a mandatory requirement for real estate agent
              registration with the Real Estate Council of Ontario (RECO). This
              requirement is established under the Trust in Real Estate Services
              Act, 2002 (TRESA) and serves as a crucial consumer protection
              measure.
            </p>
          </div>

          {/* Key Requirements Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <div className="flex items-start mb-4">
                <FileCheck className="h-6 w-6 text-emerald-600 mt-1 mr-3" />
                <h2 className="text-xl font-semibold">CRJMC Requirements</h2>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li>
                  • Must be an original document from local police service
                </li>
                <li>• Required for RECO application processing</li>
                <li>• Submit via traceable method (courier/registered mail)</li>
                <li>• Electronic submissions must be verifiable as original</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <div className="flex items-start mb-4">
                <AlertCircle className="h-6 w-6 text-emerald-600 mt-1 mr-3" />
                <h2 className="text-xl font-semibold">What's Included</h2>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li>• Criminal convictions</li>
                <li>• Youth Criminal Justice Act findings</li>
                <li>• Absolute and conditional discharges</li>
                <li>• Outstanding charges and arrest warrants</li>
                <li>• Relevant judicial orders</li>
              </ul>
            </div>
          </div>

          {/* Registration Considerations */}
          <div className="mb-12">
            <h2 className="text-2xl font-semibold mb-6">
              Registration Considerations
            </h2>
            <div className="bg-emerald-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-4">
                RECO evaluates several factors when assessing registration
                eligibility:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  {
                    icon: CheckCircle,
                    text: "Financial responsibility in business conduct",
                  },
                  {
                    icon: CheckCircle,
                    text: "Compliance with law, integrity, and honesty",
                  },
                  {
                    icon: CheckCircle,
                    text: "Accuracy of statements in applications",
                  },
                  {
                    icon: CheckCircle,
                    text: "Adherence to registration conditions",
                  },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <item.icon className="h-5 w-5 text-emerald-600" />
                    <span className="text-gray-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Important Notes */}
          <div className="bg-gray-50 p-6 rounded-lg mb-12">
            <div className="flex items-start mb-4">
              <HelpCircle className="h-6 w-6 text-emerald-600 mt-1 mr-3" />
              <h2 className="text-xl font-semibold">Important Notes</h2>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li>
                • Review RECO's "Registration Policy: Applicant/Registrant
                Criminal History" for self-assessment
              </li>
              <li>
                • Verify registration status using RECO's online registrant
                search tool
              </li>
              <li>
                • Consult with RECO if you have concerns about past conduct
                affecting eligibility
              </li>
            </ul>
          </div>

          {/* Split CTA Section */}
          <section className="relative py-16 px-4">
            <div className="container mx-auto max-w-7xl">
              <div className="grid md:grid-cols-2 overflow-hidden rounded-lg shadow-lg">
                <div className="relative h-[400px] md:h-full">
                  <picture className="object-cover translate-y-20">
                    <source
                      srcSet="/images/real-estate-agent/cleared-background-check-real-estate-agent.avif"
                      type="image/avif"
                    />
                    <source
                      srcSet="/images/real-estate-agent/cleared-background-check-real-estate-agent.webp"
                      type="image/webp"
                    />
                    <source
                      srcSet="/images/real-estate-agent/cleared-background-check-real-estate-agent.jpg"
                      type="image/jpeg"
                    />
                    <img
                      src="/images/real-estate-agent/cleared-background-check-real-estate-agent.jpg"
                      alt="Real estate agent showing cleared background check"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </picture>
                </div>
                <div className="bg-gradient-to-br from-white to-primary/5 text-gray-800 p-12 md:p-16 lg:p-24 flex items-center">
                  <div className="max-w-xl">
                    <HeadingHighlight
                      as="h2"
                      size="4xl"
                      className="mb-6 text-primary-dark text-xl sm:text-2xl md:text-3xl lg:text-4xl"
                    >
                      Get Your CRJMC Check Today
                    </HeadingHighlight>
                    <p className="text-lg text-gray-700 mb-8">
                      Fast and secure online application for Ontario real estate
                      agent checks. Perfect for both new RECO registrations and
                      renewals.
                    </p>
                    <ButtonCTA showSubheader={false} />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default RealEstateAgentsPage;

export const Head = () => [
  <SEO
    key="seo"
    title="CRJMC Ontario Background Checks for Real Estate Agents | RECO Requirements"
    description="Get your CRJMC check for Ontario Real Estate Agent Registration - fast online processing for RECO applications. Approved third party provider with 15-minute turnaround. Valid for new registrations and renewals."
    ogType="website"
    ogTitle="Ontario Real Estate Agent CRJMC Background Checks - Fast RECO Registration"
    ogDescription="Apply for your CRJMC check online. Fast background checks from an approved third party provider for Ontario Real Estate Agent Registration with RECO. Results in 15 minutes."
    ogImage="/images/real-estate-agent/cleared-background-check-real-estate-agent-512.jpg"
    twitterCard="summary_large_image"
    twitterTitle="Ontario Real Estate Agent CRJMC Background Checks | Fast Processing"
    twitterDescription="Get your CRJMC background check for Ontario Real Estate Agent Registration. Fast online processing for RECO applications and renewals."
    twitterImage="/images/real-estate-agent/cleared-background-check-real-estate-agent-512.jpg"
    canonical="https://crjmc.net/ontario/real-estate-agents"
    keywords="CRJMC, real estate agent, RECO registration, Ontario background check, criminal record check, real estate license, TRESA requirements, RECO application, real estate agent registration"
  />,
  <script
    key="schema"
    type="application/ld+json"
    dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "CRJMC Ontario Real Estate Agent Background Check Service",
        description:
          "Professional CRJMC background check service for Ontario Real Estate Agent Registration with RECO. Approved third party provider, online processing within 15 minutes.",
        provider: {
          "@type": "Organization",
          name: "CRJMC",
          "@id": "https://crjmc.net",
          url: "https://crjmc.net",
          logo: {
            "@type": "ImageObject",
            url: "https://crjmc.net/images/crjmc-net-512.png",
          },
        },
        mainEntity: {
          "@type": "Product",
          name: "CRJMC Ontario Real Estate Agent Background Check Service",
          description:
            "Professional CRJMC background check service for Ontario Real Estate Agent Registration with RECO. Approved third party provider, online processing within 15 minutes.",
          serviceType: "Background Check",
          image: {
            "@type": "ImageObject",
            url: "https://crjmc.net/images/real-estate-agent/cleared-background-check-real-estate-agent-512.jpg",
          },
          areaServed: {
            "@type": "State",
            name: "Ontario",
            addressCountry: "CA",
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: 4.9,
            reviewCount: 257,
            bestRating: 5,
            worstRating: 3,
          },
          offers: [
            {
              "@type": "Offer",
              name: "RECO Registration CRJMC Check",
              description:
                "Background check for Ontario Real Estate Agent Registration with RECO",
              price: `${CRJMC_PRICE}`,
              priceCurrency: "CAD",
              availability: "https://schema.org/InStock",
              priceValidUntil: new Date(
                new Date().setFullYear(new Date().getFullYear() + 1),
              )
                .toISOString()
                .split("T")[0],
              url: "https://crjmc.net/ontario/real-estate-agents",
              deliveryTime: "15 minutes",
              category: "Background Check Services",
            },
          ],
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "CRJMC Real Estate Agent Background Checks",
            itemListElement: [
              {
                "@type": "OfferCatalog",
                name: "New Registration Checks",
                description:
                  "CRJMC background checks for new real estate agent RECO registration",
              },
              {
                "@type": "OfferCatalog",
                name: "Registration Renewals",
                description:
                  "CRJMC background checks for real estate agent RECO registration renewals",
              },
            ],
          },
        },
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://crjmc.net",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Ontario",
              item: "https://crjmc.net/ontario",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "Real Estate Agents",
              item: "https://crjmc.net/ontario/real-estate-agents",
            },
          ],
        },
      }),
    }}
  />,
];
